import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import './Login.css';
import { getApiUrl } from '../config/api';

const PasswordReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validatePasswords = () => {
    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) return;

    if (!token) {
      setMessage('Invalid or missing reset link. Please request a new one.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(getApiUrl('/api/v1/auth/password/reset'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // No token in URL — show an error state
  if (!token) {
    return (
      <div className="page-container">
        <div className="login-wrapper">
          <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
          <div className="login-box">
            <h2 className="text-xl font-semibold text-gray-800">Invalid reset link</h2>
            <p className="text-gray-600 text-sm leading-normal mt-2">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <button
              className="mt-5 w-full p-2 bg-green-700 text-white rounded cursor-pointer hover:bg-green-800"
              onClick={() => navigate('/login')}
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="login-wrapper">
        <h1 className="text-4xl font-bold text-green-700">ZenRent</h1>
        <div className="login-box">

          {!success ? (
            <>
              <h2 className="text-xl font-semibold text-gray-800">Reset your password</h2>
              <p className="text-gray-600 text-sm leading-normal">
                Enter your new password below.
              </p>

              <form onSubmit={handleSubmit}>
                <input
                  type="password"
                  placeholder="New password (min 8 characters)"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <input
                  type="password"
                  placeholder="Confirm new password"
                  className="mt-3 w-full p-2 border rounded focus:ring-2 focus:ring-green-400"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                {passwordError && <p className="error-message mt-2">{passwordError}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="mt-4 w-full p-2 bg-green-700 text-white rounded cursor-pointer hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </button>
              </form>

              {message && <p className="error-message mt-3">{message}</p>}
            </>
          ) : (
            <div className="text-center">
              <div className="reset-success-icon bg-green-100 text-green-700">✓</div>
              <h2 className="text-xl font-semibold text-gray-800">Password reset!</h2>
              <p className="text-gray-600 text-sm leading-normal">
                Your password has been updated successfully. Redirecting to login...
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
